import React, { useState } from 'react';

import Text from '~/components/core/TextComponents/Text';
import { CheckIcon } from '~/components/icons';
import type { ClaimConductorStageHistoryResponseClaimStageHistoryEntry as StageHistoryEntry } from '~/server_shared/openapi/data-contracts';
import cn from '~/Utils/cn';

import ReturnMark from '../ReturnMark';

import { STAGE_APPEARANCE } from './constants';
import useStageBar from './useStageBar';
import { stageSentBackTo, STEP_TRANSITION_MS, stepDelays } from './utils';

interface StageBarProps {
  /** The stages the conductor is configured for; `undefined` until they load. */
  conductorStageKeys: string[] | undefined;
  /** The claim's stage changes, oldest first; empty when none is recorded or the history could not be read. */
  stageHistory: StageHistoryEntry[];
}

/**
 * The stage the claim was at before it moved to `currentKey`, kept until it moves again so that a
 * render mid-move (a claim-context update, a stream notification) keeps playing the same move.
 * `undefined` until the claim has moved, so the bar appearing is not played as the claim travelling
 * from nowhere. A key rather than an index: the stage it left can drop off the bar, shifting the rest.
 */
const useMovedFromKey = (currentKey: string | undefined): string | undefined => {
  const [move, setMove] = useState<{ from: string | undefined; to: string | undefined }>({
    from: undefined,
    to: currentKey,
  });
  if (move.to !== currentKey) {
    // Adjusting state while rendering: React re-renders straight away, before anything is painted.
    setMove({ from: move.to, to: currentKey });
  }
  return move.from;
};

/**
 * Each of the conductor's stages exactly once, including the stages ahead, and where the claim is.
 * A move is animated: the line fills towards the new stage one connector at a time and each marker's
 * colour turns as the line reaches it, and the stage the claim lands on sends out a short ring. What
 * a marker shows (tick or number) and the current label's weight switch at the start of the move, so
 * on a jump the markers in between show a tick before their colour turns.
 */
const StageBar: React.FC<StageBarProps> = ({ conductorStageKeys, stageHistory }) => {
  const steps = useStageBar(conductorStageKeys);
  const sentBackTo = stageSentBackTo(stageHistory, conductorStageKeys ?? []);
  const currentIndex = steps.findIndex((step) => step.state === 'current');
  const fromKey = useMovedFromKey(steps[currentIndex]?.key);
  const fromStepIndex = steps.findIndex((step) => step.key === fromKey);
  const fromIndex = fromStepIndex === -1 ? undefined : fromStepIndex;
  if (steps.length === 0) return null;

  return (
    <ol
      aria-label="Claim stages"
      className="m-0 flex list-none items-center border-0 border-b-1 border-solid border-slate-500 px-4 pb-12 pt-4"
    >
      {steps.map((step, index) => {
        const appearance = STAGE_APPEARANCE[step.state];
        const isFirstStep = index === 0;
        const delays = stepDelays(index, fromIndex, currentIndex);
        const markerTiming = { transitionDelay: `${delays.marker}ms`, animationDelay: `${delays.marker}ms` };

        return (
          // Every step after the first grows into its connector, so the stages spread across the bar.
          <li
            key={step.key}
            aria-current={step.state === 'current' ? 'step' : undefined}
            className={cn('flex items-center', !isFirstStep ? 'min-w-0 flex-1' : 'shrink-0')}
          >
            {!isFirstStep ? (
              <span aria-hidden className="relative mx-8 h-[1px] min-w-8 flex-1 overflow-hidden bg-slate-200">
                <span
                  data-testid="stage-connector-fill"
                  className={cn(
                    'absolute inset-y-0 left-0 bg-green-600 transition-[width] ease-linear motion-reduce:transition-none',
                    step.state === 'ahead' ? 'w-0' : 'w-full'
                  )}
                  style={{ transitionDuration: `${STEP_TRANSITION_MS}ms`, transitionDelay: `${delays.connector}ms` }}
                />
              </span>
            ) : null}
            <span className="flex shrink-0 items-center gap-8 px-4 py-2">
              <span
                className={cn(
                  'relative isolate grid h-20 w-20 shrink-0 place-items-center rounded-full transition-colors duration-300 motion-reduce:transition-none',
                  appearance.marker,
                  appearance.number
                )}
                style={markerTiming}
              >
                {step.state === 'current' && fromKey !== undefined ? (
                  // Mounts, and so rings, when the claim moves to this stage, not when the bar opens.
                  // Behind the number (`-z-10` inside the marker's `isolate` context), in front of the
                  // marker's fill.
                  <span
                    data-testid="stage-arrival-ring"
                    aria-hidden
                    className="pointer-events-none absolute inset-0 -z-10 rounded-full bg-blue-600 opacity-0 motion-safe:animate-stageArrive"
                    style={markerTiming}
                  />
                ) : null}
                {step.state === 'done' ? (
                  <CheckIcon size={12} iconColor="currentColor" />
                ) : (
                  <Text variant={Text.VARIANTS.XXS} weight={Text.WEIGHTS.SEMI_BOLD}>
                    <span className={appearance.number}>{String(index + 1)}</span>
                  </Text>
                )}
              </span>
              <Text
                variant={Text.VARIANTS.SM}
                weight={step.state === 'current' ? Text.WEIGHTS.SEMI_BOLD : Text.WEIGHTS.REGULAR}
              >
                <span
                  className={cn('transition-colors duration-300 motion-reduce:transition-none', appearance.label)}
                  style={markerTiming}
                >
                  {/* Only on the current stage: a stage behind the claim is not where the move left it. */}
                  {step.state === 'current' && step.key === sentBackTo ? <ReturnMark /> : null}
                  {step.label}
                </span>
              </Text>
            </span>
          </li>
        );
      })}
    </ol>
  );
};

export default StageBar;
